<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <v-row no-gutters align="center" class="mb-3">
      <div class="text-subtitle-1 font-weight-medium">{{ $t("finance_commissions_title") }}</div>
      <v-spacer />
      <primary-button @click="openAddDialog">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_add_item") }}
      </primary-button>
    </v-row>

    <v-row class="mb-3">
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="3">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption grey--text">{{ kpi.label }}</div>
          <div class="text-h6 font-weight-bold">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="commissionsHeaders"
        :items="entriesWithAmounts"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_commission_items')"
        item-key="id"
        class="striped-table"
        @click:row="openEntry"
      >
        <template v-slot:item.orderRef="{ item }">
          <span class="font-weight-bold">{{ item.orderRef }}</span>
        </template>
        <template v-slot:item.turnover="{ item }">{{ formatCurrency(item.turnover) }}</template>
        <template v-slot:item.rate="{ item }">{{ item.rate }}%</template>
        <template v-slot:item.commission="{ item }">{{ formatCurrency(item.commission) }}</template>
      </v-data-table>
      <p v-if="!loading && entriesWithAmounts.length > 0" class="hint-copy px-4 pb-3">
        {{ $t("finance_click_order_commission_hint") }}
      </p>
    </v-card>

    <!-- Detail dialog -->
    <v-dialog v-model="detailOpen" max-width="400">
      <v-card v-if="selectedEntry">
        <v-card-title>{{ selectedEntry.orderRef }}</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="detail-row">
            <span>{{ $t("finance_turnover") }}</span>
            <strong>{{ formatCurrency(selectedEntry.turnover) }}</strong>
          </div>
          <div class="detail-row">
            <span>{{ $t("finance_commission_with_rate", { rate: selectedEntry.rate }) }}</span>
            <strong>{{ formatCurrency(selectedEntry.commission) }}</strong>
          </div>
          <div class="detail-row">
            <span>{{ $t("finance_vat_with_rate", { rate: (VAT_RATE * 100).toFixed(0) }) }}</span>
            <strong>{{ formatCurrency(selectedEntry.vat) }}</strong>
          </div>
          <v-divider class="my-2" />
          <div class="detail-row total">
            <span>{{ $t("finance_total") }}</span>
            <strong>{{ formatCurrency(selectedEntry.total) }}</strong>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="error" @click="confirmDelete(selectedEntry)">{{ $t("finance_delete") }}</v-btn>
          <v-spacer />
          <v-btn text @click="openEditDialog(selectedEntry)">{{ $t("finance_edit") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <commission-form-dialog
      v-model="dialogOpen"
      :saving="saving"
      :entry="editingEntry"
      @save="onSaveEntry"
    />
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import GlobalConfirmBox from "@/components/global/GlobalConfirmBox.vue";
import CommissionFormDialog from "./dialogs/CommissionFormDialog.vue";

// Poslovna pravila za obračun provizije - konfiguracione konstante, ne
// podaci koji se učitavaju sa API-ja (mirroring app/config/commission.ts
// in the source app).
const VAT_RATE = 0.17;
const AGREED_RATE = 15;

export default {
  name: "FinanceTabCommissions",

  components: { GlobalConfirmBox, CommissionFormDialog },

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    saving: false,
    entries: [],
    detailOpen: false,
    selectedEntryId: null,
    dialogOpen: false,
    editingEntry: null,
    VAT_RATE,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    commissionsHeaders() {
      return [
        { text: this.$t("finance_order"), value: "orderRef" },
        { text: this.$t("finance_turnover"), value: "turnover", align: "end" },
        { text: "%", value: "rate", align: "end" },
        { text: this.$t("finance_commission"), value: "commission", align: "end" },
      ];
    },

    entriesWithAmounts() {
      return this.entries.map((entry) => {
        const commission = entry.turnover * (entry.rate / 100);
        const vat = commission * VAT_RATE;
        return {
          ...entry,
          commission,
          vat,
          total: commission + vat,
        };
      });
    },

    selectedEntry() {
      return this.entriesWithAmounts.find((e) => e.id === this.selectedEntryId) || null;
    },

    totalTurnover() {
      return this.entries.reduce((sum, e) => sum + e.turnover, 0);
    },

    totalCommission() {
      return this.entriesWithAmounts.reduce((sum, e) => sum + e.commission, 0);
    },

    totalVat() {
      return this.entriesWithAmounts.reduce((sum, e) => sum + e.vat, 0);
    },

    averageRate() {
      return this.totalTurnover > 0 ? (this.totalCommission / this.totalTurnover) * 100 : 0;
    },

    kpis() {
      return [
        { label: this.$t("finance_agreed_commission"), value: `${AGREED_RATE}%` },
        { label: this.$t("finance_charged_commission"), value: this.formatCurrency(this.totalCommission) },
        { label: this.$t("finance_average_abbr"), value: `${this.averageRate.toFixed(1)}%` },
        { label: this.$t("finance_vat"), value: this.formatCurrency(this.totalVat) },
      ];
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    openEntry(item) {
      this.selectedEntryId = item.id;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.editingEntry = null;
      this.dialogOpen = true;
    },

    openEditDialog(entry) {
      this.editingEntry = entry;
      this.detailOpen = false;
      this.dialogOpen = true;
    },

    async onSaveEntry(payload) {
      const ok = this.editingEntry
        ? await this.editEntry(this.editingEntry.id, payload)
        : await this.addEntry(payload);
      if (ok) this.dialogOpen = false;
    },

    confirmDelete(entry) {
      this.detailOpen = false;
      this.$refs.confirmBox
        .open(this.$t("finance_delete_item_title"), this.$t("finance_confirm_delete_item", { orderRef: entry.orderRef }), { color: "error" })
        .then(() => this.removeEntry(entry.id))
        .catch(() => {
          // Otkazano
        });
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/commissions`);
        this.entries = (data?.data || []).map(this.mapEntry);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_commissions_load_error"));
      } finally {
        this.loading = false;
      }
    },

    mapEntry(dto) {
      return {
        id: dto.id,
        orderRef: dto.order_ref,
        date: dto.date,
        turnover: dto.turnover,
        rate: dto.rate,
      };
    },

    async addEntry(payload) {
      if (!this.restaurantId) return false;

      this.saving = true;
      try {
        const { data } = await gpsApi.post(`/restaurants/${this.restaurantId}/commissions`, payload);
        this.entries = [this.mapEntry(data), ...this.entries];
        this.showSuccessSnackbar(this.$t("finance_item_added"));
        return true;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_item_add_error"));
        return false;
      } finally {
        this.saving = false;
      }
    },

    async editEntry(id, payload) {
      this.saving = true;
      try {
        const { data } = await gpsApi.put(`/commissions/${id}`, payload);
        const updated = this.mapEntry(data);
        this.entries = this.entries.map((e) => (e.id === id ? updated : e));
        this.showSuccessSnackbar(this.$t("finance_item_saved"));
        return true;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_item_save_error"));
        return false;
      } finally {
        this.saving = false;
      }
    },

    async removeEntry(id) {
      this.saving = true;
      try {
        await gpsApi.delete(`/commissions/${id}`);
        this.entries = this.entries.filter((e) => e.id !== id);
        this.showSuccessSnackbar(this.$t("finance_item_deleted"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_item_delete_error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.hint-copy {
  margin: 0;
  font-size: 0.78rem;
  color: #9aa4b2;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
  color: #6b7685;
  padding: 4px 0;
}

.detail-row.total {
  font-size: 1.05rem;
  color: #0b1220;
}

.detail-row.total strong {
  color: #00b37e;
}
</style>
